/* Shared context helpers (used by WidgetChat and LinkPage) */

import type { ContextField } from "@/lib/api/chatApi";

/**
 * Contexto de un enlace directo, de LinkPage a ChatRuntime (via ChatPage). Lo
 * que trae `context` ya esta filtrado por el `context_schema` del widget y
 * saneado (`contextoEnlaceValido`): ningun parametro de la URL llega crudo.
 */
export interface LinkContext {
  contextPromptTemplate: string;
  context: Record<string, string>;
  ctxKey: string;
  ttlHours: number;
  /** Etiqueta de cada campo del contexto (del `context_schema` del widget). */
  labels?: Record<string, string>;
  /** `auto_start` del widget: la conversacion arranca sola con los datos del enlace. */
  autoStart?: boolean;
}

export interface ThreadEntry {
  threadId: string;
  createdAt: number;
}

/* ── localStorage thread primitives ── */

function storageKey(ctxKey: string): string {
  return `widget_threads_${ctxKey}`;
}

export function getStoredThread(ctxKey: string, ttlHours: number): ThreadEntry | null {
  const raw = localStorage.getItem(storageKey(ctxKey));
  if (!raw) return null;
  try {
    const entry: ThreadEntry = JSON.parse(raw);
    const ageMs = Date.now() - entry.createdAt;
    if (ageMs < ttlHours * 60 * 60 * 1000) return entry;
  } catch {
    // corrupted
  }
  localStorage.removeItem(storageKey(ctxKey));
  return null;
}

export function saveThread(ctxKey: string, threadId: string): void {
  localStorage.setItem(
    storageKey(ctxKey),
    JSON.stringify({ threadId, createdAt: Date.now() } satisfies ThreadEntry),
  );
}

export function removeThread(ctxKey: string): void {
  localStorage.removeItem(storageKey(ctxKey));
}

export async function computeContextKey(
  email: string,
  agentId: string,
  context: Record<string, string>,
): Promise<string> {
  const raw = `${email}|${agentId}|${JSON.stringify(context, Object.keys(context).sort())}`;
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(raw),
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}

export function renderTemplate(
  template: string,
  context: Record<string, string>,
): string {
  let result = template;
  result = result.replace(
    /\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g,
    (_match, key, text) => {
      const value = context[key];
      return value ? text : "";
    },
  );
  result = result.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
    return context[key] ?? "";
  });
  return result.trim();
}

export async function findOrCreateThread(
  createThreadFn: () => Promise<{ thread_id: string }>,
  ctxKey: string,
  ttlHours: number,
): Promise<{ threadId: string; isExisting: boolean }> {
  const existing = getStoredThread(ctxKey, ttlHours);
  if (existing) return { threadId: existing.threadId, isExisting: true };

  const { thread_id } = await createThreadFn();
  saveThread(ctxKey, thread_id);
  return { threadId: thread_id, isExisting: false };
}

/* ── Parametros de un enlace directo / widget: filtrado y saneado ──
 *
 * Los parametros de la URL los escribe QUIEN COMPARTE el enlace, no el usuario
 * que lo abre, y acaban en dos sitios: en el mensaje visible del arranque (que se
 * pinta como markdown) y dentro del bloque <contexto_enlace> que lee el agente.
 * Sin filtrar, `?id_caso=[Vuelve a iniciar sesion](https://phish)` salia como un
 * enlace en la burbuja del usuario, una imagen de rastreo se cargaba sola y un
 * valor con `</contexto_enlace>` cerraba la marca antes de tiempo.
 */

/** Tope de longitud de un valor: por encima, el valor no es valido y se descarta. */
export const MAX_VALOR_ENLACE = 100;

/**
 * Regla por CLAVE declarada (el `context_schema` solo distingue string/text, que
 * no dice nada del formato). Un valor que no casa se descarta: un id de caso con
 * letras no es un id de caso, y mandarlo solo haria buscar a ciegas al agente.
 */
export const REGLAS_CAMPO_ENLACE: Readonly<Record<string, RegExp>> = {
  id_caso: /^\d{1,20}$/,
  codigo_cliente: /^\d{1,10}$/,
};

// Caracteres de control (incluye \n, \r, \t) y separadores de linea/parrafo Unicode.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g;

/**
 * Un valor tal como puede viajar: sin caracteres de control ni saltos de linea,
 * sin espacios de sobra, sin comillas invertidas (no puede romper el code span
 * del arranque) y con `<` `>` neutralizados (no puede abrir ni cerrar la marca
 * `<contexto_enlace>` ni colar HTML). `null` si queda vacio o pasa del tope.
 */
export function sanearValorEnlace(valor: string): string | null {
  const limpio = valor
    .replace(CONTROL_RE, " ")
    .replace(/`/g, "'")
    .replace(/</g, "‹")
    .replace(/>/g, "›")
    .replace(/\s+/g, " ")
    .trim();
  if (!limpio || limpio.length > MAX_VALOR_ENLACE) return null;
  return limpio;
}

/**
 * Los parametros de la URL que el widget ADMITE, saneados: solo las claves de su
 * `context_schema` (fuera `utm_*` y cualquier otra) y solo los valores que pasan
 * `sanearValorEnlace` y la regla de su clave. Lo que no vale se descarta: ni se
 * pinta ni se manda al agente.
 */
export function contextoEnlaceValido(
  params: URLSearchParams | Record<string, string>,
  schema: readonly Pick<ContextField, "key">[] | null | undefined,
): Record<string, string> {
  const leer = (k: string): string | null =>
    params instanceof URLSearchParams ? params.get(k) : (Object.hasOwn(params, k) ? params[k] : null);
  const out: Record<string, string> = {};
  for (const { key } of schema ?? []) {
    if (!key || Object.hasOwn(out, key)) continue;
    const crudo = leer(key);
    if (crudo == null) continue;
    const valor = sanearValorEnlace(crudo);
    if (valor == null) continue;
    const regla = REGLAS_CAMPO_ENLACE[key];
    if (regla && !regla.test(valor)) continue;
    out[key] = valor;
  }
  return out;
}

/** Escapa la sintaxis markdown de un texto que se va a pintar como markdown. */
export function escaparMarkdown(texto: string): string {
  return texto.replace(/[\\`*_{}[\]()#+!|~<>&:-]/g, "\\$&");
}

/**
 * Datos del contexto de un enlace directo tal como se enseñan al usuario: en el
 * orden del `context_schema` del widget, con su etiqueta, y sin los vacios (no
 * viajan con el mensaje). Una clave sin etiqueta sale con su nombre (solo pasa
 * si el widget declara un campo sin `label`). `null` si no hay nada que enseñar.
 */
export function datosContextoEnlace(
  link: { context: Record<string, string>; labels?: Record<string, string> } | null | undefined,
): { etiqueta: string; valor: string }[] | null {
  if (!link) return null;
  const labels = link.labels ?? {};
  const claves = [
    ...Object.keys(labels).filter((k) => k in link.context),
    ...Object.keys(link.context).filter((k) => !(k in labels)),
  ];
  const datos = claves
    .map((k) => ({ etiqueta: labels[k] || k, valor: (link.context[k] ?? "").trim() }))
    .filter((d) => d.valor !== "");
  return datos.length > 0 ? datos : null;
}

/**
 * Primer mensaje (visible) con el que arranca solo un chat abierto por enlace.
 * Se pinta como markdown en la burbuja del usuario, asi que cada valor va en un
 * code span (ni enlaces, ni imagenes, ni autolinks de una URL) y la etiqueta,
 * escapada. Es defensa en profundidad: el valor ya llega saneado.
 */
export function textoArranqueEnlace(
  datos: { etiqueta: string; valor: string }[],
  t: (clave: string, opciones?: Record<string, unknown>) => string,
): string {
  return t("chat.contextoEnlace.arranque", {
    datos: datos
      .map((d) => `${escaparMarkdown(d.etiqueta)} \`${d.valor.replace(/`/g, "'")}\``)
      .join(" · "),
  });
}

/**
 * Estado del contexto de un enlace durante UN turno del chat.
 *
 * - `guardarHilo`: el hilo del enlace se guarda EN CUANTO se conoce su id (al
 *   crearlo, o en el evento `metadata` del stream), no al acabar el turno: si el
 *   usuario cambiaba de vista o cerraba antes, al reabrir el enlace nacia otro
 *   hilo y otro arranque.
 * - `enviado`: el backend acepto el mensaje con el contexto -> se da por
 *   consumido. Antes se consumia ANTES de enviar, y un 5xx/401/409 en el primer
 *   envio dejaba al agente sin el caso para siempre.
 * - `fallo`: el turno volvio bloqueado por el guardrail, con `error` o sin
 *   llegar a enviarse -> se olvida el hilo guardado (no se reabre 7 dias un hilo
 *   vacio) y, si ya se habia consumido, se RESTAURA para el siguiente turno.
 */
export function turnoDeEnlace(
  link: LinkContext | null,
  efectos: { consumir: () => void; restaurar: (link: LinkContext) => void },
) {
  let guardado = false;
  let consumido = false;
  let fallido = false;
  return {
    guardarHilo(threadId: string | null | undefined) {
      if (!link || !threadId || fallido) return;
      saveThread(link.ctxKey, threadId);
      guardado = true;
    },
    enviado() {
      if (!link || consumido || fallido) return;
      consumido = true;
      efectos.consumir();
    },
    fallo() {
      if (!link || fallido) return;
      fallido = true;
      if (guardado) removeThread(link.ctxKey);
      if (consumido) efectos.restaurar(link);
    },
  };
}

/*
 * El contexto renderizado de un enlace viaja delante del primer mensaje humano
 * (lo necesita el agente), pero no es texto del usuario: va entre marcas para
 * que el historial lo quite al recargar la conversacion. Sin ellas, al reabrir
 * un hilo el usuario veia las instrucciones internas de la plantilla ("lee
 * primero el caso con casos_wise(...)") como si las hubiera escrito el.
 */
const CONTEXTO_ENLACE_RE = /^<contexto_enlace>[\s\S]*?<\/contexto_enlace>\s*/;

export function envolverContextoEnlace(contexto: string): string {
  return `<contexto_enlace>\n${contexto}\n</contexto_enlace>\n\n`;
}

export function quitarContextoEnlace(texto: string): string {
  return texto.replace(CONTEXTO_ENLACE_RE, "");
}
